/**
 * useTalents — React hook for the local player's talent state.
 *
 * SERVER AUTHORITY:
 *   - `player.skillPoints` is the available-point projection from
 *     `talentStateUpdate`; earned points are derived from authoritative level.
 *   - `player.talents` is rebuilt from the canonical class tree plus the
 *     server's persisted rank map.
 *
 * The talent *tree definitions* are real (`src/shared/classes/TalentTrees.ts`).
 * This hook surfaces the live player's copy, falling back to the canonical
 * per-class definition when a given player snapshot has no `talents` populated,
 * so the window always renders the real tree (never a mock).
 *
 * Allocation dispatches through the canonical UI action bridge. The hook does
 * not mutate ranks optimistically; only a later server snapshot updates state.
 */

import { useGame } from '../contexts/GameContext';
import { TALENT_TREES } from '../../../shared/classes/TalentTrees';
import type { CharacterClass, TalentTree } from '../../../shared/CharacterTypes';
import type { ProgressionCostOfferWire } from '../../../shared/types/ProgressionCost';
import { uiActionBridge } from '../framework/uiActionBridge';

export interface TalentsState {
  /** Server-authoritative available talent points. */
  skillPoints: number;
  /** The player's class — selects which talent tree to display. */
  className: CharacterClass | null;
  /**
   * The player's allocated talent tree — real per-node rank/cost/prereq/effect
   * data. Falls back to the canonical `TALENT_TREES[class]` definition when the
   * live player snapshot has no `talents` populated.
   */
  talents: TalentTree;
  /**
   * True when there is no spendable-point pool AND no defined tree to show —
   * lets the window render an honest empty state rather than a blank grid.
   */
  hasTree: boolean;
  /** Live owner-only offer and wallet used only to render action truth. */
  talentRespecOffer: ProgressionCostOfferWire | null;
  gold: number;
  /** Request one immutable rank allocation; the server validates everything. */
  spendTalent: (treeName: string, nodeId: string) => void;
  /** Request a server-authoritative reset; no local rank or gold mutation. */
  respecTalents: () => void;
}

function resolveTree(
  className: CharacterClass | null,
  playerTalents: TalentTree | undefined,
): TalentTree {
  // Prefer the live player's copy when it carries any tree...
  if (playerTalents && Object.keys(playerTalents).length > 0) return playerTalents;
  // ...otherwise fall back to the canonical per-class definition.
  if (className && TALENT_TREES[className]) return TALENT_TREES[className];
  return {};
}

export function useTalents(): TalentsState {
  // useGame is the canonical source the window already reads from; this hook is
  // a thin, typed projection — no new poll/subscription needed since the game
  // context re-renders consumers on player updates.
  const { player } = useGame();

  const className = player?.class ?? null;
  const talents = resolveTree(className, player?.talents);

  return {
    skillPoints: player?.skillPoints ?? 0,
    className,
    talents,
    hasTree: Object.keys(talents).length > 0,
    talentRespecOffer: player?.progressionCostOffers?.talentRespec ?? null,
    gold: player?.inventory?.gold ?? 0,
    spendTalent: (treeName, nodeId) => {
      uiActionBridge.dispatch('spendTalent', { treeName, nodeId });
    },
    respecTalents: () => {
      uiActionBridge.dispatch('respecTalents', {});
    },
  };
}
